import { useEffect, useState, lazy, Suspense } from 'react';
import Typewriter from "typewriter-effect";
import { FadeUp } from "../common/ScrollReveal.js";
import { ModalSkeleton } from "../common/PageSkeleton.js";
import HeroImageCollage from "./HeroImageCollage.js";

// Lazy load components for better performance
const GoogleReviewSection = lazy(() => import("./GoogleReviewSection.js"));
const ServiceCard = lazy(() => import("./ServiceCard.js"));
const Section4 = lazy(() => import("./Section4.js"));
const LogoSection = lazy(() => import("./LogoSection.js"));
const HomeFAQs = lazy(() => import("./HomeFAQs.js"));
const WhatWeDoSection = lazy(() => import("./WhatWeDoSection.js"));
const ContactForm = lazy(() => import("../ContactForm/ContactFormModal.js"));

const Home = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
        const response = await fetch(`${apiBase}/api/cms/home`);
        const result = await response.json();
        if (result.success && result.data) {
          setContent(result.data);
        }
      } catch (error) {
        console.error("Failed to load Home page content:", error);
      }
    };
    fetchContent();
  }, []);

  const heroLine1 = content?.hero?.heroLine1 || 'Grow Your U.S. Business with Trusted';
  const heroSubtitle = content?.hero?.heroSubtitle || '';
  const typewriterStrings = content?.hero?.typewriterStrings?.length
    ? content.hero.typewriterStrings
    : 'Bookkeeping,CFO Advisory,USA Company Formation,Tax Return'.split(',').map((s) => s.trim()).filter(Boolean);
  const ctaPrimary = content?.hero?.ctaPrimary || 'Book a Free Consultation';
  const ctaSecondary = content?.hero?.ctaSecondary || 'View Our Pricing';
  const ctaPrimaryLink = content?.hero?.ctaPrimaryLink || '#contact';
  const ctaSecondaryLink = content?.hero?.ctaSecondaryLink || '/pricing';
  const heroBody = content?.hero?.heroBody || 'Starting and growing a business in the United States brings new opportunities—but as your business expands, managing tax filing, <a href="/bookkeeping" class="text-[#38bdf8] font-semibold no-underline hover:text-[#38bdf8]/80 transition-colors duration-300">bookkeeping</a> and compliance may start to slow you down. BooksComply helps founders, startups, and small companies keep things simple. From <a href="/usa-company-formation" class="text-[#38bdf8] font-semibold no-underline hover:text-[#38bdf8]/80 transition-colors duration-300">U.S. company formation</a> to daily accounting, we make every step easier so you stay focused on success.';
  useEffect(() => {
    const createdMeta = [];

    const setMetaTag = ({ attribute, value, content }) => {
      const selector = `meta[${attribute}="${value}"]`;
      let meta = document.head.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
        createdMeta.push(meta);
      }
      meta.setAttribute('content', content);
    };

    const upsertCanonical = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      return link;
    };

    const createJsonLd = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      return script;
    };

    if (!'') {
      document.title = 'Bookkeeping, Tax & Company Formation Services in USA | BooksComply';
    }

    setMetaTag({
      attribute: 'name',
      value: 'description',
      content: 'Streamline bookkeeping, tax filing, and company formation in the USA. Cost-effective, compliant services powered by expert U.S. accountants.'
    });
    setMetaTag({
      attribute: 'name',
      value: 'keywords',
      content: 'US bookkeeping outsourcing, US tax filing partner, virtual CFO USA, USA company incorporation support, BooksComply accountants'
    });
    setMetaTag({ attribute: 'name', value: 'robots', content: 'index, follow' });
    setMetaTag({ attribute: 'property', value: 'og:title', content: 'Bookkeeping, Tax & Company Formation Services in USA | BooksComply' });
    setMetaTag({ attribute: 'property', value: 'og:description', content: 'Streamline bookkeeping, tax filing, and company formation in the USA. Cost-effective, compliant services powered by expert U.S. accountants.' });
    setMetaTag({ attribute: 'property', value: 'og:image', content: 'https://www.bookscomply.com/Assets/Images/Front_Image.png' });
    setMetaTag({ attribute: 'property', value: 'og:url', content: 'https://www.bookscomply.com/' });
    setMetaTag({ attribute: 'name', value: 'twitter:card', content: 'summary_large_image' });

    const canonicalLink = upsertCanonical('https://www.bookscomply.com/');

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'BooksComply',
      url: 'https://www.bookscomply.com',
      logo: 'https://www.bookscomply.com/Assets/Images/HD%20Quality%20Logo.svg',
      description: 'BooksComply provides end-to-end US accounting, bookkeeping, tax filing, and incorporation solutions for founders worldwide.',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91 9428482371',
        contactType: 'customer service',
        areaServed: 'USA'
      },
      sameAs: [
        'https://www.linkedin.com/company/bookscomply',
        'https://x.com/njp_advisors'
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '124'
      }
    };

    const servicesSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'BooksComply USA Expansion Services',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/tax-return',
            name: 'Tax Return Services USA',
            description: 'Forget the tax season headaches. Our team will prepare and file your federal and state returns and guide you on every required form.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Tax Preparation',
            serviceOutput: 'Filed US federal and state tax returns with compliance support',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/tax-return',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/bookkeeping',
            name: 'U.S. Bookkeeping Services',
            description: 'Stay prepared with dependable bookkeeping services USA. Outsource your bookkeeping to our team, and enjoy the good financial reports and reliable compliance.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Bookkeeping Service',
            serviceOutput: 'GAAP-compliant monthly financial statements',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/bookkeeping',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/cfo-advisory',
            name: 'Virtual CFO Services USA for Startups',
            description: "Grow the same with confidence. With our virtual CFO services for startups USA, you'll gain niche financial insights and better decision-making—without hiring a CFO.",
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Financial Advisory Service',
            serviceOutput: 'Strategic finance models and investor-ready dashboards',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/cfo-advisory',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Service',
            '@id': 'https://www.bookscomply.com/usa-company-formation',
            name: 'USA Company Formation Services',
            description: 'Build the U.S. business incorporation simple. We guide you through state selection, document preparation, EIN registration, and IRS compliance.',
            provider: {
              '@type': 'Organization',
              name: 'BooksComply',
              url: 'https://www.bookscomply.com'
            },
            areaServed: {
              '@type': 'Country',
              name: 'United States'
            },
            serviceType: 'Business Incorporation Service',
            serviceOutput: 'Formed US legal entity with compliance roadmap',
            offers: {
              '@type': 'Offer',
              url: 'https://www.bookscomply.com/usa-company-formation',
              priceCurrency: 'USD',
              price: 'Contact for a tailored quote'
            }
          }
        }
      ]
    };

    const reviewSchema = {
      '@context': 'https://schema.org',
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: 'Chirag Darji'
      },
      itemReviewed: {
        '@type': 'Organization',
        name: 'BooksComply'
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5'
      },
      reviewBody: 'BooksComply handled our Wyoming incorporation, filings, and ongoing compliance with speed and transparency.'
    };

    const organizationScript = createJsonLd('home-organization-schema', organizationSchema);
    const servicesScript = createJsonLd('home-services-schema', servicesSchema);
    const reviewScript = createJsonLd('home-review-schema', reviewSchema);

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How to start a company in the USA?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "BooksComply guides you through every state's requirements—step-by-step. Stay informed at each stage with regular updates."
          }
        },
        {
          '@type': 'Question',
          name: 'How long does U.S. company formation take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most businesses are set up in just a few days. Non-resident founders may experience a longer wait for EIN registration.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can I outsource U.S. tax filing if I live abroad?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes—you can! BooksComply supports expat entrepreneurs worldwide, managing federal and state filings remotely.'
          }
        },
        {
          '@type': 'Question',
          name: 'What are the most affordable bookkeeping services in the USA for startups?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Many startups look for bookkeeping that is budget-friendly yet reliable. Services like BooksComply specialize in affordable bookkeeping solutions tailored for small businesses and startups'
          }
        }
      ]
    };
    const faqScript = createJsonLd('home-faq-schema', faqSchema);

    return () => {
      createdMeta.forEach((meta) => document.head.removeChild(meta));
      [organizationScript, servicesScript, reviewScript, faqScript].forEach((script) => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      if (canonicalLink && canonicalLink.parentNode) {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
    };
  }, []);

  const handleCloseContactForm = () => {
    setShowContactForm(false);
  };

  return (
    <main className="bg-white dark:bg-[#0B0F19] text-black dark:text-slate-100 transition-colors duration-200 font-['Inter','Poppins','Segoe_UI',-apple-system,BlinkMacSystemFont,'Roboto','Helvetica_Neue',sans-serif]">
      {/* Hero Section */}
      <section
        id="homeSection"
        className="relative min-h-[55vh] flex items-center pt-5 pb-8 sm:pt-7 sm:pb-10 md:pt-9 md:pb-12 lg:pt-11 lg:pb-16 xl:pt-14 xl:pb-20 overflow-hidden bg-gradient-to-br from-[#020c1b] via-[#061834] to-[#0b2447] text-white"
      >
        {/* Animated mesh gradient background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.28),_transparent_60%)]" />
          <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_center,_rgba(14,165,233,0.45),_transparent_70%)] blur-3xl" />
          {/* Subtle dot grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px'}}></div>
        </div>

        {/* Floating geometric particles */}
        <div className="absolute top-[15%] left-[8%] w-2 h-2 rounded-full bg-blue-400/40 float-particle"></div>
        <div className="absolute top-[25%] right-[12%] w-1.5 h-1.5 rounded-full bg-orange-400/35 float-particle-alt"></div>
        <div className="absolute bottom-[20%] left-[15%] w-2.5 h-2.5 rounded-full bg-blue-400/30 float-particle" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-[60%] right-[20%] w-1.5 h-1.5 rounded-full bg-orange-400/30 float-particle-alt" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-[40%] left-[3%] w-3 h-3 rotate-45 bg-blue-400/20 float-particle" style={{animationDelay: '1s'}}></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 lg:gap-14 items-center">
            {/* Left Column: Taglines */}
            <FadeUp className="">
              {/* Frosted glass trust badge */}
              <div className="inline-flex items-center gap-2 mt-0 mb-5 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 shadow-none">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                <span className="text-xs sm:text-sm font-medium text-slate-300 tracking-wide">Trusted by 500+ Founders Worldwide</span>
              </div>

              <h1 className="text-[1.75rem] sm:text-[2rem] md:text-[2.25rem] lg:text-[2.5rem] xl:text-[3rem] font-extrabold mb-4 leading-tight">
                <span className="text-white">{heroLine1}</span>
                <br />
                <span className="bg-gradient-to-r from-[#60a5fa] via-[#38bdf8] to-[#fb923c] gradient-text-animated font-extrabold text-[1.5rem] sm:text-[1.75rem] md:text-[2rem] lg:text-[2.25rem] xl:text-[2.75rem] leading-tight block min-h-[2.25rem] sm:min-h-[2.5rem] md:min-h-[3rem] lg:min-h-[3.25rem] xl:min-h-[4rem]">
                  <Typewriter
                    key={typewriterStrings.join(',')}
                    options={{
                      strings: typewriterStrings,
                      autoStart: true,
                      loop: true,
                      deleteSpeed: 0.5,
                    }}
                  />
                </span>
              </h1>
              {heroSubtitle && (
                <p className="text-[0.95rem] sm:text-base md:text-[1.05rem] lg:text-lg mt-2 text-white/80 leading-relaxed">
                  {heroSubtitle}
                </p>
              )}
              <p 
                className="text-[0.95rem] sm:text-base md:text-[1.05rem] lg:text-lg mt-4 text-white/80 leading-relaxed max-w-[540px]"
                dangerouslySetInnerHTML={{ __html: heroBody }}
              />
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mt-7">
                <button
                  className="group w-full sm:w-auto min-w-[200px] relative bg-gradient-to-r from-[#ff6b00] to-[#e65c00] text-white font-semibold rounded-full px-7 py-3.5 cta-glow transition-all duration-300 hover:-translate-y-1 hover:from-[#ff7b1a] hover:to-[#f06000] focus:outline-none focus:ring-2 focus:ring-[#ff6b00]/40 focus:ring-offset-2 dark:focus:ring-offset-[#0B0F19] overflow-hidden"
                  onClick={() => ctaPrimaryLink === '#contact' ? setShowContactForm(true) : (window.location.href = ctaPrimaryLink)}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {ctaPrimary}
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </span>
                  {/* Shine sweep effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{animation: 'shine-sweep 1.5s ease-in-out'}}></div>
                </button>
                <button
                  className="group w-full sm:w-auto min-w-[150px] relative bg-white/10 backdrop-blur-md text-white font-semibold rounded-full px-7 py-3.5 border border-white/15 hover:border-[#38bdf8]/30 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1 shadow-none"
                  onClick={() => ctaSecondaryLink === '#contact' ? setShowContactForm(true) : (window.location.href = ctaSecondaryLink)}
                >
                  <span className="flex items-center justify-center gap-2">
                    {ctaSecondary}
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </span>
                </button>
              </div>
            </FadeUp>

            {/* Right Column: Hero Image Collage */}
            <HeroImageCollage />
          </div>
        </div>
      </section>

      {/* Lazy loaded sections with min-height fallback to prevent CLS */}
      <Suspense fallback={
        <div style={{ minHeight: '2000px' }} className="animate-pulse bg-slate-50 dark:bg-dark-900">
          <div className="py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="h-8 bg-slate-200 dark:bg-dark-700 rounded w-1/3 mx-auto mb-6"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <div key={i} className="h-64 bg-slate-100 dark:bg-[#161D30] rounded-2xl"></div>)}
              </div>
            </div>
          </div>
        </div>
      }>
        <ServiceCard content={content?.servicesSection} />
        <WhatWeDoSection content={content?.advantageSection} />
        <GoogleReviewSection content={content?.reviewSection} />
        <Section4 content={content?.approachSection} />
        <LogoSection content={content?.integrationsSection} />
        <HomeFAQs content={content?.faqSection} />
      </Suspense>

      {/* Contact Form Modal */}
      {showContactForm && (
        <Suspense fallback={<ModalSkeleton />}>
          <ContactForm onClose={handleCloseContactForm} />
        </Suspense>
      )}
    </main>
  );
};
export default Home;